import DirectAccess from "@husky-hrc/Components/Icons/DirectAccess";
import LeadTimes from "@husky-hrc/Components/Icons/LeadTimes";
import ReduceCosts from "@husky-hrc/Components/Icons/ReduceCosts";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    items = "items",
}

const enum icons {
    directAccess = "Direct Access",
    leadTimes = "Lead Times",
    reduceCosts = "Reduce Costs",
}

interface Props extends WidgetProps {
    fields: {
        [fields.items]: ValuePropModel[];
    };
}

interface ValuePropModel {
    fields: {
        heading: string;
        text: string;
        icon: string;
    };
}

export interface ValuePropsStyles {
    listItemContainerStyles?: InjectableCss;
    listItemContainerStylesMobile?: InjectableCss;
    listGroupStyles?: InjectableCss;
    listGroupStylesMobile?: InjectableCss;
    listItemStyles?: InjectableCss;
    iconStyles?: IconProps;
    itemHeaderStyle?: TypographyPresentationProps;
    itemHeaderStyleMobile?: TypographyPresentationProps;
    itemParagraphStyle?: TypographyPresentationProps;
    itemParagraphStyleMobile?: TypographyPresentationProps;
}

export const valuePropsStyles: ValuePropsStyles = {
    listItemContainerStyles: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        max-width: 100%;
                        padding: 0 100px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 0 235px;
                    `,
                ]);
            }}
        `,
    },

    listItemContainerStylesMobile: {
        css: css`
            list-style: none;
        `,
    },

    listGroupStyles: {
        css: css`
            background: ${getColor("common.background")};
            color: ${getColor("text.main")};
            padding: 35px 0;
            margin-left: 30;
            list-style: none;
            display: flex;
            gap: 30px;
        `,
    },
    listGroupStylesMobile: {
        css: css`
            background: ${getColor("common.background")};
            color: ${getColor("text.main")};
            list-style: none;
            border-radius: 6px;
            padding-left: 25px;
            padding-right: 25px;
            padding-top: 50px;
        `,
    },

    listItemStyles: {
        css: css`
            padding-bottom: 20px;
            display: flex;
            gap: 27px;
            flex-grow: 1;
            flex-basis: 0;
        `,
    },

    iconStyles: {
        size: 55,
        css: css`
            align-items: flex-start;
            float: left;
            height: 86px;
            width: 70px;
        `,
    },
    itemHeaderStyle: {
        variant: "h3",
        forwardAs: "h3",
        color: "danger.main",
        weight: 900,
        css: css`
            margin-bottom: 0.5rem;
            line-height: 34px;
            letter-spacing: 0.8px;
        `,
    },
    itemHeaderStyleMobile: {
        variant: "h4",
        forwardAs: "h4",
        color: "danger.main",
        weight: 900,
        css: css`
            margin-bottom: 0.5rem;
            line-height: 24px;
            letter-spacing: 0.6px;
        `,
    },
    itemParagraphStyle: {
        variant: "p",
        forwardAs: "p",
        size: `${getProp("theme.typography.p.size")};`, // TODO: Should this reside in style guide somewhere?
        color: "text.main",
        weight: "bold", // TODO: Should this reside in style guide somewhere?
        lineHeight: "22px",
        css: css`
            letter-spacing: 0.8px;
            font-size: ${getProp("theme.typography.p.size")};
        `,
    },
    itemParagraphStyleMobile: {
        variant: "p",
        forwardAs: "p",
        size: "theme.typography.p.size",
        color: "text.main",
        lineHeight: "26px",
        css: css`
            letter-spacing: 0.8px;
            font-size: ${getProp("theme.typography.p.size")};
        `,
    },
};

const styles = valuePropsStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.directAccess:
            return DirectAccess;
        case icons.leadTimes:
            return LeadTimes;
        case icons.reduceCosts:
            return ReduceCosts;
        default:
            break;
    }

    return "";
};

const ValueProps: React.FC<Props> = ({ fields }) => {
    const valuePropsList = (
        <UnorderedList {...styles.listGroupStyles}>
            {fields.items &&
                fields.items.map((valueProp, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ListItem key={index} {...styles.listItemStyles}>
                        <Icon {...{ ...styles.iconStyles, src: GetIcon(valueProp.fields.icon) }} />
                        <StyledWrapper>
                            <Typography {...styles.itemHeaderStyle}>{valueProp.fields.heading}</Typography>
                            <Typography {...styles.itemParagraphStyle}>{valueProp.fields.text}</Typography>
                        </StyledWrapper>
                    </ListItem>
                ))}
        </UnorderedList>
    );

    const valuePropsListMobile = (
        <UnorderedList {...styles.listGroupStylesMobile}>
            {fields.items &&
                fields.items.map((valueProp, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ListItem key={index} {...styles.listItemStyles}>
                        <Icon {...{ ...styles.iconStyles, src: GetIcon(valueProp.fields.icon) }} />
                        <StyledWrapper>
                            <Typography {...styles.itemHeaderStyleMobile}>{valueProp.fields.heading}</Typography>
                            <Typography {...styles.itemParagraphStyleMobile}>{valueProp.fields.text}</Typography>
                        </StyledWrapper>
                    </ListItem>
                ))}
        </UnorderedList>
    );

    return (
        <>
            <Hidden below="xl">
                <StyledWrapper {...styles.listItemContainerStyles}>{valuePropsList}</StyledWrapper>
            </Hidden>

            <Hidden above="lg">
                <StyledWrapper {...styles.listItemContainerStylesMobile}>{valuePropsListMobile}</StyledWrapper>
            </Hidden>
        </>
    );
};

const defaultValue: ValuePropModel[] = [
    {
        fields: {
            heading: "Reduced Costs",
            text: "Access to discounts, volume discount agreements, and loyalty rewards.",
            icon: icons.reduceCosts,
        },
    },
    {
        fields: {
            heading: "Better Lead Times",
            text: "Expedited lead times means we can start building and you can get producing.",
            icon: icons.leadTimes,
        },
    },
    {
        fields: {
            heading: "Direct Access",
            text: "Have an issue? Not sure where to begin? Get direct access to our team of global tech experts.",
            icon: icons.directAccess,
        },
    },
];

const definition: WidgetDefinition = {
    group: "Testing", // TODO: Correct category; decide as a team
    fieldDefinitions: [
        {
            name: fields.items,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.heading;
            },
            defaultValue,
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "heading",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "text",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [{ value: icons.directAccess }, { value: icons.leadTimes }, { value: icons.reduceCosts }],
                    defaultValue: "",
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ValueProps,
    definition,
};

export default widgetModule;
